import { ProductSlug } from "@/api/product-api";
import { syncCart } from "@/api/CartApi";
import { CurrencyIsoCode } from "@/enums";
import { useCurrency } from "@/hooks/useCurrency";
import { EssentialGameItem } from "@/models/orders/OrderModelV2";
import { BundleMerger } from "@/store/cart-store/bundle-merger";
import { CartStoreUtils } from "@/store/cart-store/cart-store-utils";
import { useLocale } from "@/store/locale-store";
import { getBundledState } from "@/store/cart-store/useBundledStore";
import { analyticsInstance } from "@/libs/analytics";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import { CartToken } from "@/libs/cart-token/CartToken";
import { CartUtil } from "@/utils/CartUtil";
import FbPixel from "@/utils/FbPixel";
import { ProductUtil } from "@/utils/ProductUtil";
import { packSetLines } from "@/libs/cart/pack-set-lines";
import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { Product } from "@/api/product-api";
import type {
  CartItem,
  CartItemOption,
  CartItemWithOptionalName,
} from "@/store/cart-store/type";
export type { CartItemOption, CartItem } from "@/store/cart-store/type";

import { AddToCartResult } from "@/store/cart-store/type";
import { useEffect, useMemo } from "react";
import {
  useGameBundles,
  useProducts,
} from "@/services/product/useProductService";
import { useSellableGames } from "@/hooks/useBhapticsContents";
export { AddToCartResult } from "@/store/cart-store/type";

/**
 * localStorage에 저장하기 때문에 가격이나 할인 등의 민감한 정보는 저장하지 않습니다.
 */

type CartUIState = {
  isCartOpen: boolean;
  isBulkOrder: boolean;
};

type CartState = {
  items: CartItem[];
  gameItems: EssentialGameItem[];
};

type CartAction = {
  showCart: () => void;
  hideCart: () => void;
  addGameItem: (item: EssentialGameItem) => void;
  setGameItems: (items: EssentialGameItem[]) => void;
  setDeviceItems: (items: CartItem[]) => void;
  deleteGameItem: (index: number) => void;
  deleteGameItems: (indexes: number[]) => void;
  add: (
    item: {
      slug: ProductSlug;
      quantity?: number;
      options?: CartItemOption[];
      sku: string;
    },
    shouldOpenCart?: boolean,
  ) => void;
  addDeviceItem: (item: {
    slug: ProductSlug;
    quantity: number;
    sku: string;
    options: CartItemOption[];
  }) => void;
  increase: (sku: string) => void;
  decrease: (sku: string) => void;
  delete: (sku: string) => void;
  clear: () => void;
  setIsBulkOrder: (isBulkOrder: boolean) => void;
};

export type Cart = CartUIState & CartState & CartAction;

const cartStore = create<Cart>()(
  devtools(
    persist(
      (set, get) => ({
        isCartOpen: false,
        isBulkOrder: false,
        items: [],
        gameItems: [],
        showCart: () => {
          // 카트가 시트에서 페이지로 전환됨: 열기 트리거는 모두 /cart 페이지로 이동
          if (typeof window !== "undefined") {
            import("next/router").then(({ default: Router }) => {
              Router.push("/cart");
            });
          }
        },
        setIsBulkOrder: (isBulkOrder) => {
          set((state) => {
            return {
              isBulkOrder: isBulkOrder,
            };
          });
        },
        setGameItems: (items) => set({ gameItems: items }),
        setDeviceItems: (items) => set({ items }),
        hideCart: () => set({ isCartOpen: false }),
        addGameItem: (item) => {
          set((state) => ({
            isCartOpen: true,
            gameItems: [...state.gameItems, item],
          }));
        },
        deleteGameItem: (index) => {
          set((state) => {
            const gameItems = state.gameItems.filter((_, i) => i !== index);
            return {
              gameItems,
            };
          });
        },

        deleteGameItems: (indexes) => {
          set((state) => {
            const gameItems = state.gameItems.filter(
              (_, i) => !indexes.includes(i),
            );
            return {
              gameItems,
            };
          });
        },
        addDeviceItem: (item: {
          slug: ProductSlug;
          quantity: number;
          sku: string;
          options: CartItemOption[];
        }) => {
          set((state) => {
            const sku = item.sku;
            const existingItem = state.items.find((_item) => _item.sku === sku);
            const newItems = existingItem
              ? [
                  {
                    ...existingItem,
                    quantity: existingItem.quantity + item.quantity,
                  },
                  ...state.items.filter((_item) => _item.sku !== sku),
                ]
              : [
                  {
                    sku: item.sku,
                    slug: item.slug,
                    quantity: item.quantity,
                    options: item.options,
                  },
                  ...state.items,
                ];
            return {
              items: newItems,
            };
          });
        },
        add: (
          { slug, quantity = 1, options = [], sku },
          shouldOpenCart = true,
        ) => {
          set((state) => {
            const existingItem = state.items.find((item) => item.sku === sku);
            const newItems = existingItem
              ? [
                  {
                    ...existingItem,
                    quantity: existingItem.quantity + quantity,
                  },
                  ...state.items.filter((item) => item.sku !== sku),
                ]
              : [{ sku, slug, quantity, options }, ...state.items];

            return {
              isCartOpen: shouldOpenCart,
              items: newItems,
            };
          });
        },

        delete: (sku) => {
          set((state) => {
            const items = state.items.filter((item) => item.sku !== sku);
            return {
              items,
            };
          });
        },

        increase: (sku) => {
          set((state) => {
            const item = state.items.find((item) => item.sku === sku);
            if (!item) return {};

            return {
              items: state.items.map((item) =>
                item.sku === sku
                  ? {
                      ...item,
                      quantity: Math.min(
                        item.quantity + 1,
                        CartStoreUtils.MAX_QUANTITY,
                      ),
                    }
                  : item,
              ),
            };
          });
        },

        decrease: (sku) => {
          set((state) => {
            const item = state.items.find((item) => item.sku === sku);
            if (!item || item.quantity <= 1) return {};

            return {
              items: state.items.map((item) =>
                item.sku === sku
                  ? { ...item, quantity: item.quantity - 1 }
                  : item,
              ),
            };
          });
        },

        clear: () => {
          set({
            isCartOpen: false,
            isBulkOrder: false,
            items: [],
            gameItems: [],
          });
        },
      }),
      {
        name: "cart-store_0313",
        version: 1,
      },
    ),
  ),
);

export const useCartStore = <T>(selector: (state: Cart) => T): T =>
  cartStore(selector);

export const getCartState = () => cartStore.getState();

const useProductsMap = (): Map<ProductSlug, Product> => {
  const products = useProducts();

  return useMemo(() => {
    if (!products.data) return new Map();
    return new Map(products.data.map((product) => [product.slug, product]));
  }, [products.data]);
};

const reportItemsChange = async (
  prevCartItems: CartItemWithOptionalName[],
  currentCartItems: CartItemWithOptionalName[],
  productsMap: Map<string, Product>,
  currency: CurrencyIsoCode,
  countryCode: string | null,
  /**
   * `add_to_cart` 의 진입점 (#5292). **늘어난 항목에만** 실린다 — 삭제 경로는 안 받는다.
   *
   * ⚠️ 이 함수는 이전/현재 카트의 **차이만** 본다. 누가 왜 늘렸는지는 여기서 알 수 없어
   * 담기 API 가 넘겨준다. 안 넘기면 `unknown` — 계측 안 된 경로가 GA 에서 드러난다.
   */
  trigger: CartTrigger = "unknown",
) => {
  try {
    const increasedItems = CartUtil.getIncreasedItems(
      prevCartItems,
      currentCartItems,
    );
    const decreasedItems = CartUtil.getDecreasedItems(
      prevCartItems,
      currentCartItems,
    );

    /**
     * ★**분해값을 그대로 넘긴다** (#5190). 종전엔 여기서 순가(`originalAmount -
     * discountAmount`) 하나로 접어 GA4 item 의 `price` 에 넣었는데, `view_item`·`purchase` 는
     * `price`=정가 + `discount`=할인액 규약이라 **같은 제품의 item 매출이 단계마다 다른 값**
     * 으로 잡혔다. 규약 정본은 `Analytics.ts` 의 `ITEM_PRICE_CONVENTION`.
     *
     * Meta 는 content 레벨 `value` 하나만 받고 그 값은 **순가**가 맞으므로 접어서 넘긴다 —
     * 두 트래커의 스키마가 달라서지 값이 어긋나는 게 아니다.
     */
    const getItemPrice = (slug: string) => {
      const product = productsMap.get(slug);
      if (!product) return undefined;
      return (
        ProductUtil.getProductPrice({ currency, countryCode, product }) ??
        undefined
      );
    };
    const netOf = (price?: {
      originalAmount: number;
      discountAmount: number;
    }) => (price ? price.originalAmount - price.discountAmount : undefined);

    const promises: Promise<void>[] = [];
    for (const item of increasedItems) {
      const price = getItemPrice(item.slug);
      promises.push(
        Promise.all([
          analyticsInstance?.addItem({
            slug: item.slug,
            quantity: item.quantity,
            name: item.name,
            sku: item.sku,
            price: price?.originalAmount,
            discount: price?.discountAmount,
            currency,
            trigger,
          }),
          FbPixel.addItem({
            slug: item.slug,
            quantity: item.quantity,
            name: item.name,
            sku: item.sku,
            value: netOf(price),
            currency,
          }),
        ]).then(() => {}),
      );
    }
    for (const item of decreasedItems) {
      const price = getItemPrice(item.slug);
      promises.push(
        Promise.all([
          analyticsInstance?.removeItem({
            slug: item.slug,
            sku: item.sku,
            quantity: item.quantity,
            name: item.name,
            price: price?.originalAmount,
            discount: price?.discountAmount,
            currency,
          }),
          FbPixel.removeItem({
            slug: item.slug,
            sku: item.sku,
            quantity: item.quantity,
            name: item.name,
            value: netOf(price),
            currency,
          }),
        ]).then(() => {}),
      );
    }
    await Promise.allSettled(promises);
  } catch (error) {
    console.error("Error reporting cart items change:", error);
  }
};

const addNames = (items: CartItem[], productsMap: Map<string, Product>) => {
  return items.map((p) => {
    const product = productsMap.get(p.slug);
    return {
      ...p,
      name: product?.name || "",
    };
  });
};

/**
 * 쓰기 **직후** 카트의 수량 세트를 접는다(MOLLE Pouch 2개 → 세트 1개). 근거·멱등성은
 * `libs/cart/pack-set-lines` 의 docblock. 세트가 **새로 생겼으면** 그 slug 를 돌려준다 —
 * 호출처가 「번들로 합쳐졌다」 표면(`MergedToBundle`)을 띄울지 정한다.
 *
 * ⚠️ `reportItemsChange` 의 `current` 를 읽기 **전에** 부른다. 뒤에 부르면 분석에는 단품 2개가,
 * 카트에는 세트 1개가 남아 `add_to_cart` 와 실제 라인이 갈린다.
 */
const packCartSets = (
  products: Product[] | undefined,
  currency: CurrencyIsoCode,
  countryCode: string | null,
): ProductSlug | null => {
  if (!products) return null;
  const state = cartStore.getState();
  const packed = packSetLines(state.items, products, { currency, countryCode });
  const unchanged =
    packed.length === state.items.length &&
    packed.every(
      (l, i) =>
        l.sku === state.items[i].sku && l.quantity === state.items[i].quantity,
    );
  if (unchanged) return null;

  const qtyOf = (items: CartItem[], sku: string) =>
    items.find((i) => i.sku === sku)?.quantity ?? 0;
  const grown = packed.find(
    (l) =>
      !state.items.some((i) => i.sku === l.sku) ||
      qtyOf(packed, l.sku) > qtyOf(state.items, l.sku),
  );
  state.setDeviceItems(packed);
  return grown ? grown.slug : null;
};

/**
 * 카트를 **화면에 보일 때** 수량 세트 기준으로 다시 정리한다 (#5906 R1 1번).
 *
 * 쓰기 훅들의 `packCartSets` 는 **쓰는 순간의** 가격·재고로만 판정한다. 세트를 담은 뒤
 * 가격이 바뀌거나(세트가 더는 안 쌈) 세트가 품절되면, 카트에 다시 쓰기 전까지 세트 줄이
 * 그대로 남아 결제된다 — persist 카트라 며칠 뒤일 수 있다. 그래서 카탈로그·통화·국가·카트가
 * 바뀔 때마다 한 번 더 지나게 한다. `packSetLines` 가 멱등이라 바뀐 게 없으면 아무것도 안 쓴다.
 *
 * ⚠️ **결제 직전이 아니라 여기**인 이유: 결제 직전에만 고치면 사용자가 본 카트($27 세트)와
 * 청구(단품 2개)가 화면 확인 없이 갈린다. 여기서 고치면 카트 화면이 먼저 바뀐다.
 * ⚠️ `_app` 의 `CartSetNormalizer` 가 유일한 호출처다 — `QueryClientProvider` 안쪽이어야 한다
 * (`useProducts`, docs/pitfalls.md #1(Header 는 QueryClientProvider 밖에서 렌더된다)).
 */
export const useNormalizeCartSets = () => {
  const productsMap = useProductsMap();
  const products = useProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const items = useCartStore((s) => s.items);

  useEffect(() => {
    if (!products.data) return;
    const before = cartStore.getState().items;
    packCartSets(products.data, currency, countryCode);
    const after = cartStore.getState().items;
    if (after === before) return;
    reportItemsChange(
      addNames(before, productsMap),
      addNames(after, productsMap),
      productsMap,
      currency,
      countryCode,
    ).catch(() => {});
  }, [products.data, currency, countryCode, items, productsMap]);
};

export const useAddItem = () => {
  const productsMap = useProductsMap();
  const products = useProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (
    item: {
      slug: ProductSlug;
      quantity?: number;
      options?: CartItemOption[];
      sku: string;
    },
    shouldCartOpen: boolean = true,
    /** `add_to_cart` 진입점 (#5292). 안 주면 `unknown` — 계측 공백이 GA 에 드러난다. */
    trigger: CartTrigger = "unknown",
    /**
     * **어느 갈래를 탔는지** 돌려준다 (#5366). 종전에는 `void` 라 호출처가 담겼는지 알 수
     * 없었고, 그 위에 "담겼다" 는 UI 를 얹으면 **사실과 다른 말을 한다.**
     *
     * 갈래마다 이미 자기 표면이 있다 — 여기서 새로 띄우지 않고 **사실만** 알린다:
     *   · `OverLimit`      → `setIsBulkOrder(true)` = Purchase Limit 다이얼로그
     *   · `MergedToBundle` → `showCart()` = `/cart` 이동
     *   · `Success`        → **아무 표면도 없다**(`isCartOpen` 은 시트 폐지 후 죽은 플래그)
     *                        ← #5366 이 고치는 무음이 정확히 이 갈래다
     *
     * ⚠️ 형제 `useAddDevice` 는 이미 `AddToCartResult | null` 을 돌려준다 — 같은 축인데
     * 한쪽만 `void` 여서 호출처가 갈렸다. 여기서 맞춘다.
     */
  ): AddToCartResult => {
    const state = cartStore.getState();
    if (
      CartStoreUtils.isOverItemLimit(state.items, {
        sku: item.sku,
        quantity: item.quantity ?? 1,
      })
    ) {
      state.setIsBulkOrder(true);
      return AddToCartResult.OverLimit;
    }

    const previous = addNames(state.items, productsMap);

    // 번들 병합 체크
    const bundleResult = products.data
      ? BundleMerger.makeNonGameBundles({
          items: state.items,
          candidate: { ...item, quantity: item.quantity ?? 1, sku: item.sku },
          products: products.data,
          currency,
          countryCode,
        })
      : null;

    let result: AddToCartResult;
    if (bundleResult) {
      state.setDeviceItems(bundleResult.items);
      getBundledState().setBundle({
        bundle: bundleResult.combinedResult,
      });
      if (shouldCartOpen) state.showCart();
      result = AddToCartResult.MergedToBundle;
    } else {
      state.add(item, shouldCartOpen);
      result = AddToCartResult.Success;
    }
    const packedSet = packCartSets(products.data, currency, countryCode);
    if (packedSet) {
      getBundledState().setBundle({ bundle: packedSet });
      if (shouldCartOpen && result !== AddToCartResult.MergedToBundle) {
        state.showCart();
      }
      result = AddToCartResult.MergedToBundle;
    }

    const current = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previous,
      current,
      productsMap,
      currency,
      countryCode,
      trigger,
    ).catch(() => {});
    return result;
  };
};

/** 동일 SKU의 아이템을 수량 합산하여 병합 */
const mergeDeviceItemsBySku = (items: CartItem[]): CartItem[] => {
  return items.reduce((acc, item) => {
    const existing = acc.find((i) => i.sku === item.sku);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      acc.push({ ...item });
    }
    return acc;
  }, [] as CartItem[]);
};

export const useAddDevice = () => {
  const productsMap = useProductsMap();
  const gameBundles = useGameBundles();
  const products = useProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (
    item: CartItem,
    /** `add_to_cart` 진입점 (#5292). 안 주면 `unknown` — 계측 공백이 GA 에 드러난다. */
    trigger: CartTrigger = "unknown",
  ): AddToCartResult | null => {
    const state = cartStore.getState();

    if (CartStoreUtils.isOverItemLimit(state.items, item)) {
      return AddToCartResult.OverLimit;
    }

    const previous = addNames(state.items, productsMap);

    // 디바이스 번들 병합 체크
    const bundleResult = products.data
      ? BundleMerger.makeNonGameBundles({
          items: state.items,
          candidate: item,
          products: products.data,
          currency,
          countryCode,
        })
      : null;

    let response: AddToCartResult;

    if (bundleResult) {
      state.setDeviceItems(bundleResult.items);
      getBundledState().setBundle({
        bundle: bundleResult.combinedResult,
      });
      response = AddToCartResult.MergedToBundle;
    } else {
      // 게임 번들 체크
      const gameBundleResult = BundleMerger.makeGameBundles({
        gameItems: state.gameItems,
        deviceItems: [item, ...state.items],
        bundles: gameBundles.data || [],
      });

      if (gameBundleResult.bundles.length > 0) {
        const deviceItems = mergeDeviceItemsBySku([
          ...gameBundleResult.bundles,
          ...gameBundleResult.remainingDeviceItems,
        ]);

        state.setGameItems(gameBundleResult.remainingGameItems);
        state.setDeviceItems(deviceItems);
        getBundledState().setBundle({
          bundle: gameBundleResult.bundles[0].slug,
        });
        response = AddToCartResult.MergedToBundle;
      } else {
        state.addDeviceItem(item);
        response = AddToCartResult.Success;
      }
    }
    const packedSet = packCartSets(products.data, currency, countryCode);
    if (packedSet) {
      getBundledState().setBundle({ bundle: packedSet });
      response = AddToCartResult.MergedToBundle;
    }

    const current = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previous,
      current,
      productsMap,
      currency,
      countryCode,
      trigger,
    ).catch(() => {});

    return response;
  };
};

export const useAddAccessories = () => {
  const productsMap = useProductsMap();
  const products = useProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (
    items: CartItem[],
    /** `add_to_cart` 진입점 (#5292). 안 주면 `unknown` — 계측 공백이 GA 에 드러난다. */
    trigger: CartTrigger = "unknown",
  ): AddToCartResult | null => {
    const previousItems = addNames(cartStore.getState().items, productsMap);

    let hasOverLimit = false;
    for (const item of items) {
      if (CartStoreUtils.isOverItemLimit(cartStore.getState().items, item)) {
        hasOverLimit = true;
        continue;
      }
      cartStore.getState().addDeviceItem(item);
    }
    // PDP 액세서리 행에서 파우치 2개 → 세트. 결과는 Success 그대로 둔다 — 이 경로의 호출처
    // (`addAccessoriesToCart`)는 반환값을 안 보고, 표면은 장비 담기(`addDevice`)가 띄운다.
    packCartSets(products.data, currency, countryCode);

    const currentItems = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previousItems,
      currentItems,
      productsMap,
      currency,
      countryCode,
      trigger,
    ).catch(() => {});

    return hasOverLimit ? AddToCartResult.OverLimit : AddToCartResult.Success;
  };
};

export const useAddCartStoreItem = () => {
  const productsMap = useProductsMap();
  const products = useProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (
    item: CartItem,
    /** `add_to_cart` 진입점 (#5292). 안 주면 `unknown` — 계측 공백이 GA 에 드러난다. */
    trigger: CartTrigger = "unknown",
  ): {
    isBulkOrder: boolean;
    added: boolean;
    /**
     * 담은 직후 수량 세트로 **접혔나**(MOLLE Pouch 단품 2개 → 세트 1개 · `packCartSets`). 접혔으면
     * 방금 담은 SKU 줄이 카트에서 사라지므로 호출처는 담기 완료 팝을 띄우지 않는다 — 그 팝은 SKU 로
     * 카트 줄을 찾아 카드가 0장인 팝이 된다(#5945 R1). 대신 여기서 전역 병합 다이얼로그
     * (`BundleMergedDialog` — 「MOLLE Pouch … (Set) applied」)를 연다. 게임 번들 병합과 같은 표면이다.
     * ⚠️ 형제 `useAddItem` 의 세트 묶기와는 **표면이 다르다** — 그쪽은 다이얼로그를 열지 않고(`isOpen` 없음)
     * `showCart()` = `/cart` 로 보낸다(#5945 R2). 같은 것은 「묶이면 담기 완료 팝을 띄우지 않는다」는 판정뿐이다.
     * 그쪽으로 MOLLE 를 담는 표면은 주로 `/cart` 의 추천 카드라 이동이 사실상 제자리여서 맞추지 않았다.
     */
    merged: boolean;
  } => {
    const items = cartStore.getState().items;

    if (CartStoreUtils.isOverItemLimit(items, item)) {
      cartStore.getState().setIsBulkOrder(true);
      return {
        isBulkOrder: true,
        added: false,
        merged: false,
      };
    }

    const previous = addNames(cartStore.getState().items, productsMap);
    cartStore.getState().addDeviceItem(item);
    const packedSet = packCartSets(products.data, currency, countryCode);
    if (packedSet) {
      getBundledState().setBundle({ bundle: packedSet, isOpen: true });
    }
    const current = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previous,
      current,
      productsMap,
      currency,
      countryCode,
      trigger,
    ).catch(() => {});

    return {
      isBulkOrder: false,
      added: true,
      merged: !!packedSet,
    };
  };
};

export const useDeleteItem = () => {
  const productsMap = useProductsMap();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (sku: string) => {
    const previous = addNames(cartStore.getState().items, productsMap);
    cartStore.getState().delete(sku);
    const current = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previous,
      current,
      productsMap,
      currency,
      countryCode,
    ).catch(() => {});
  };
};

export const useIncreaseItem = () => {
  const productsMap = useProductsMap();
  const products = useProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (sku: string) => {
    const state = cartStore.getState();
    const item = state.items.find((i) => i.sku === sku);
    if (!item) return;

    if (CartStoreUtils.isOverItemLimit(state.items, { sku, quantity: 1 })) {
      state.setIsBulkOrder(true);
      return;
    }

    const previous = addNames(state.items, productsMap);
    state.increase(sku);
    // 카트에서 파우치 1 → 2 로 올려도 세트가가 적용돼야 한다(PDP 에서 2개 담은 것과 같은 결과).
    packCartSets(products.data, currency, countryCode);
    const current = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previous,
      current,
      productsMap,
      currency,
      countryCode,
    ).catch(() => {});
  };
};

export const useDecreaseItem = () => {
  const productsMap = useProductsMap();
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (sku: string) => {
    const state = cartStore.getState();
    const item = state.items.find((i) => i.sku === sku);
    if (!item || item.quantity <= 1) return;

    const previous = addNames(state.items, productsMap);
    state.decrease(sku);
    const current = addNames(cartStore.getState().items, productsMap);
    reportItemsChange(
      previous,
      current,
      productsMap,
      currency,
      countryCode,
    ).catch(() => {});
  };
};

export const useAddGameItem = () => {
  const sellableGames = useSellableGames();
  const currency = useCurrency();
  const gameBundles = useGameBundles();

  return async (
    item: EssentialGameItem,
    /**
     * `add_to_cart` 진입점 (#5292). **표면이 정한다** — 이 훅을 장바구니 게임키 추천,
     * 게임 상세의 키 판매, 상점 목록이 함께 쓴다.
     */
    trigger: CartTrigger = "unknown",
  ) => {
    const result = BundleMerger.makeGameBundles({
      gameItems: [item, ...cartStore.getState().gameItems],
      deviceItems: cartStore.getState().items,
      bundles: gameBundles.data || [],
    });
    const deviceItems = [
      ...result.bundles,
      ...result.remainingDeviceItems,
    ].reduce((acc, item) => {
      const foundIndex = acc.findIndex((i) => i.sku === item.sku);
      if (foundIndex === -1) {
        acc.push(item);
        return acc;
      } else {
        acc[foundIndex].quantity += item.quantity;
      }
      return acc;
    }, [] as CartItem[]);
    cartStore.getState().setGameItems(result.remainingGameItems);
    cartStore.getState().setDeviceItems(deviceItems);
    cartStore.getState().showCart();

    if (result.bundles.length > 0) {
      getBundledState().setBundle({
        bundle: result.bundles[0].slug,
        isOpen: true,
      });
    }

    const gameResell = sellableGames.gameResells.find(
      (resell) => resell.id === item.gameResellId,
    );
    if (!gameResell) return;

    const gameItem = CartUtil.getGameItem({
      gameResell,
      platform: item.platform,
      currency,
    });
    analyticsInstance?.addGameItem({ gameItem, currency, trigger });
    FbPixel.addGameItem({ gameItem, currency });
  };
};

export const useDeleteGameItem = () => {
  const sellableGames = useSellableGames();
  const currency = useCurrency();

  // ★index 대신 **{gameResellId, platform} 키**로도 지울 수 있게 한다.
  //
  // `CartUtil.getGameItems` 가 통화 미등록 라인을 **압축 제거**하므로(#5179 §4), 화면이 그
  // 압축된 목록의 index 로 삭제를 요청하면 raw `cartStore.gameItems` 에서는 **다른 게임이
  // 지워진다**. 같은 게임+플랫폼이 N 개면 서로 동일한 라인(각 1 키)이라 첫 매치를 지우면 된다.
  return async (
    target: number | { gameResellId: string; platform: string },
  ) => {
    const gameItems = cartStore.getState().gameItems;
    const index =
      typeof target === "number"
        ? target
        : gameItems.findIndex(
            (g) =>
              g.gameResellId === target.gameResellId &&
              g.platform === target.platform,
          );
    if (index < 0) return;
    const targetItem = gameItems[index];
    if (!targetItem) return;
    cartStore.getState().deleteGameItem(index);

    const gameResell = sellableGames.gameResells.find(
      (resell) => resell.id === targetItem.gameResellId,
    );
    if (!gameResell) return;

    const gameItem = CartUtil.getGameItem({
      gameResell,
      platform: targetItem.platform,
      currency,
    });
    analyticsInstance?.deleteGameItem({ gameItem, currency });
    FbPixel.deleteGameItem({ gameItem, currency });
  };
};

export const useDeleteGameItems = () => {
  const sellableGames = useSellableGames();
  const currency = useCurrency();

  return async (indexes: number[]) => {
    const gameItems = cartStore.getState().gameItems;
    const targetItems = indexes.map((index) => gameItems[index]);
    cartStore.getState().deleteGameItems(indexes);

    targetItems.forEach((targetItem) => {
      const gameResell = sellableGames.gameResells.find(
        (resell) => resell.id === targetItem.gameResellId,
      );
      if (!gameResell) return;

      const gameItem = CartUtil.getGameItem({
        gameResell,
        platform: targetItem.platform,
        currency,
      });
      analyticsInstance?.deleteGameItem({ gameItem, currency });
      FbPixel.deleteGameItem({ gameItem, currency });
    });
  };
};

// 장바구니 변경 시 서버 동기화 (fire-and-forget). device items + standalone 게임 모두.
cartStore.subscribe((state, prevState) => {
  if (
    state.items !== prevState.items ||
    state.gameItems !== prevState.gameItems
  ) {
    syncCart(
      state.items.map((i) => ({ sku: i.sku, quantity: i.quantity })),
      // cart-store.gameItems = standalone 게임(EssentialGameItem). 번들 게임은 device SKU 서버 재파생(§5.6).
      // 서버 확정 형식은 {gameResellId, platform}(식별자만) — disableCountryList 등은 전송 안 함.
      state.gameItems.map((g) => ({
        gameResellId: g.gameResellId,
        platform: g.platform,
      })),
    );
  }
});

// Global-E cart token 생애주기 — docs/global-e-storefront.md §4.5
// 빈 → 채워짐: token 발급, 채워짐 → 빈: token 삭제.
// persist 재수화도 동일 경로로 처리되므로 별도 init 불필요.
cartStore.subscribe((state, prevState) => {
  const prevEmpty =
    prevState.items.length === 0 && prevState.gameItems.length === 0;
  const currEmpty = state.items.length === 0 && state.gameItems.length === 0;

  if (prevEmpty && !currEmpty) {
    CartToken.ensure();
  } else if (!prevEmpty && currEmpty) {
    CartToken.clear();
  }
});
